import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SessionService} from '../../services/session/session.service';
import {ActivatedRoute, Router} from '@angular/router';
// @ts-ignore
import packageInfo from '../../../../package.json';
import { AppSettings } from '../../app.settings';
import { UiAlertService } from '../../services/ui-alert/ui-alert.service';
import { BrandsService } from '../../services/brands/brands.service';
import { CodeInputComponent } from 'angular-code-input';
import { ConfirmValidator } from '../../validators/confirm-validator';
import { UsersService } from '../../services/users/users.service';
import { Partner, PartnersService, SignupBrandContext } from '../../services/partners/partners.service';
import { ManagedFeatureFlagsService } from '../../services/managed-feature-flags/managed-feature-flags.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

const PAYROLL_ADMIN_SIGNUP_PROFILE_REDIRECT_FLAG = 'PAYROLL_ADMIN_SIGNUP_PROFILE_REDIRECT';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignUpComponent implements OnInit, OnDestroy {
  @ViewChild('codeInput') codeInput !: CodeInputComponent;

  public form: UntypedFormGroup;
  public error = '';
  public info = packageInfo.version;
  public logoUrl = '';
  protected readonly AppSettings = AppSettings;

  public isIntegrationAdmin = AppSettings.isIntegration();
  public isContentAdmin = AppSettings.isContentAdmin();
  public isCustomerSupport = AppSettings.isCustomerSupport();
  public isBrokerAdmin = AppSettings.isBroker();
  public isPayrollAdmin = AppSettings.isPayrollAdmin();
  public canUsePayrollAdminSignup = this.isPayrollAdmin && AppSettings.isPayrollAdminSignupAllowed();
  hidePassword = true;

  public partner?: Partner;
  public partners: Partner[] = [];
  public brandContextLoading = false;
  public partnersLoading = false;
  public inviteDerivedBrand = false;
  public isSignupSubmitting = false;
  private partnerId?: string;
  private signupEmailSubscription?: Subscription;
  private lastBrandContextEmail = '';
  private brandContextRequestId = 0;

  public signUpTitle = this.isCustomerSupport ? 'Customer Support Sign Up' :
    this.isIntegrationAdmin ? 'Integration Admin Sign Up' :
    this.isBrokerAdmin ? 'Broker Admin Sign Up' :
    this.isContentAdmin ? 'Content Admin Sign Up' :
    this.isPayrollAdmin ? 'Direct Benefits Admin Sign Up' : 'Team Admin Sign Up';

  constructor(
      private formBuilder: UntypedFormBuilder,
      private sessionService: SessionService,
      private router: Router,
      private route: ActivatedRoute,
      private uiAlertService: UiAlertService,
      private brandsService: BrandsService,
      private usersService: UsersService,
      private partnersService: PartnersService,
      private managedFeatureFlagsService: ManagedFeatureFlagsService,
  ) {
    this.form = this.formBuilder.group({
      username: new UntypedFormControl('', [Validators.required]),
      brand: new UntypedFormControl('', this.isPayrollAdmin ? [Validators.required] : []),
      password: new UntypedFormControl('', [ Validators.required, Validators.pattern(/^(?=\D*\d)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z]).{12,30}$/)  ]),
      confirmPassword: new UntypedFormControl('', [ Validators.required, Validators.minLength(6) ]),
    });
    ConfirmValidator.twoFieldsEqual(this.form, 'password', 'confirmPassword');
  }

  async ngOnInit(): Promise<void> {
    if (!this.canUsePayrollAdminSignup) {
      void this.router.navigate(['/login']);
      return;
    }

    this.configurePayrollAdminBrandControl();
    await this.loadPartnerOptions();
    this.brandsService.init().then(() => {
      this.logoUrl = this.brandsService.getLogoOnWhite();
      this.route.params.subscribe(params => {
        if (params.showTimeout === 'true') {
          this.uiAlertService.presentMessage('You have been logged out due to inactivity');
          this.router.navigate(['login']);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.signupEmailSubscription?.unsubscribe();
  }

  async signUpClicked(): Promise<any> {
    if (this.isSignupSubmitting) {
      return;
    }

    this.error = '';
    await this.refreshSignupBrandContext();
    this.updateSelectedPartnerFromForm();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error = this.getValidationErrorMessage();
      return;
    }

    const data = {
      email: this.form.get('username')?.value,
      username: this.form.get('username')?.value,
      password: '',
      partnerId: this.partnerId,
      signupUserId: this.usersService.getCurrentUserId(),
      source: 'payroll-admin-direct-signup',
    };
    data.password = this.form.get('password')?.value;

    this.isSignupSubmitting = true;
    try {
      await this.sessionService.signUpAndLogin(data);
      const navigationTarget = await this.getPostSignupNavigation();
      const didNavigate = navigationTarget.extras
        ? await this.router.navigate(navigationTarget.commands, navigationTarget.extras)
        : await this.router.navigate(navigationTarget.commands);
      if (!didNavigate) {
        this.isSignupSubmitting = false;
      }
      // TODO: get working
      // this.usersService.updateUserBrand(result.userId, partnerId).then(() => {
      //   void this.router.navigate([this.sessionService.loginUrl ? this.sessionService.loginUrl : '']);
      // });
    } catch (error: any) {
      console.error('Error in signup method', error);
      this.error = this.isIntegrationAdmin ? error?.error?.errors[0]?.message : error?.error?.errors;
      this.isSignupSubmitting = false;
    }
  }

  private getValidationErrorMessage(): string {
    const usernameControl = this.form.get('username');
    if (usernameControl?.hasError('required')) {
      return 'Email is required.';
    }

    const passwordControl = this.form.get('password');
    if (passwordControl?.hasError('required')) {
      return 'Password is required.';
    }
    if (passwordControl?.hasError('pattern')) {
      return 'Password must be 12-30 characters and include uppercase, lowercase, and a number.';
    }

    const confirmPasswordControl = this.form.get('confirmPassword');
    if (confirmPasswordControl?.hasError('required')) {
      return 'Please confirm your password.';
    }
    if (confirmPasswordControl?.hasError('mismatch')) {
      return 'Passwords do not match.';
    }

    const brandControl = this.form.get('brand');
    if (brandControl?.hasError('required')) {
      return 'Brand is required.';
    }

    return 'Please review the form and try again.';
  }

  private async getPostSignupNavigation(): Promise<{commands: any[]; extras?: any}> {
    const defaultCommands = [this.sessionService.loginUrl ? this.sessionService.loginUrl : ''];
    if (!this.isPayrollAdmin) {
      return { commands: defaultCommands };
    }

    try {
      const redirectToProfile = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_SIGNUP_PROFILE_REDIRECT_FLAG,
        'team-admin',
        {
          userId: this.sessionService.getUserId(),
          partnerId: this.partnerId,
        },
      );

      if (redirectToProfile) {
        return {
          commands: ['profile'],
          extras: {
            queryParams: {
              fromPayrollSignup: 'true',
            },
          },
        };
      }
    } catch (error) {
      console.error('Error evaluating payroll signup redirect flag', error);
    }

    return { commands: defaultCommands };
  }

  private configurePayrollAdminBrandControl(): void {
    const brandControl = this.form.get('brand');
    if (!brandControl) {
      return;
    }

    brandControl.setValidators([Validators.required]);
    brandControl.updateValueAndValidity();
    this.signupEmailSubscription = this.form.get('username')?.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => {
        void this.refreshSignupBrandContext();
      });
  }

  private async loadPartnerOptions(): Promise<void> {
    this.partnersLoading = true;
    try {
      const partners = await this.partnersService.getAllPartners();
      this.partners = (partners || [])
        .filter((partner) => !!partner?.partnerId && partner?.name !== 'Wallit')
        .sort((a, b) => this.getPartnerLabel(a).localeCompare(this.getPartnerLabel(b)));
    } catch (error) {
      console.error('Error loading signup brands', error);
      this.error = 'Unable to load brands.';
    } finally {
      this.partnersLoading = false;
    }
  }

  private async refreshSignupBrandContext(): Promise<void> {
    const email = this.normalizeEmail(this.form.get('username')?.value);
    if (!email) {
      this.lastBrandContextEmail = '';
      if (this.inviteDerivedBrand) {
        this.inviteDerivedBrand = false;
        this.partner = undefined;
        this.partnerId = undefined;
        this.form.get('brand')?.setValue('', { emitEvent: false });
        this.setBrandControlEnabled(true);
      }
      return;
    }

    if (email === this.lastBrandContextEmail) {
      return;
    }

    this.lastBrandContextEmail = email;
    const requestId = ++this.brandContextRequestId;
    this.brandContextLoading = true;

    try {
      const context = await this.partnersService.lookupSignupBrandContext(email);
      if (requestId !== this.brandContextRequestId) {
        return;
      }
      this.applySignupBrandContext(context);
    } catch (error) {
      console.error('Error loading signup brand context', error);
      if (requestId === this.brandContextRequestId) {
        this.inviteDerivedBrand = false;
        this.setBrandControlEnabled(true);
      }
    } finally {
      if (requestId === this.brandContextRequestId) {
        this.brandContextLoading = false;
      }
    }
  }

  private applySignupBrandContext(context: SignupBrandContext | null): void {
    const contextPartner = context?.brand;
    const contextPartnerId = `${contextPartner?.partnerId || ''}`.trim();
    if (!contextPartnerId) {
      const wasInviteDerivedBrand = this.inviteDerivedBrand;
      this.inviteDerivedBrand = false;
      this.partner = undefined;
      if (wasInviteDerivedBrand) {
        this.form.get('brand')?.setValue('', { emitEvent: false });
      }
      this.partnerId = `${this.form.get('brand')?.value || ''}`.trim() || undefined;
      this.setBrandControlEnabled(true);
      return;
    }

    this.inviteDerivedBrand = true;
    this.partner = this.ensurePartnerOption(contextPartner as Partner);
    this.partnerId = contextPartnerId;
    this.form.get('brand')?.setValue(contextPartnerId, { emitEvent: false });
    this.setBrandControlEnabled(false);
  }

  private ensurePartnerOption(partner: Partner): Partner {
    const partnerId = `${partner?.partnerId || ''}`.trim();
    const existing = this.partners.find((candidate) => `${candidate?.partnerId || ''}`.trim() === partnerId);
    if (existing) {
      return existing;
    }

    this.partners = [...this.partners, partner]
      .sort((a, b) => this.getPartnerLabel(a).localeCompare(this.getPartnerLabel(b)));
    return partner;
  }

  private setBrandControlEnabled(enabled: boolean): void {
    const brandControl = this.form.get('brand');
    if (!brandControl) {
      return;
    }

    if (enabled) {
      brandControl.enable({ emitEvent: false });
      return;
    }

    brandControl.disable({ emitEvent: false });
  }

  private updateSelectedPartnerFromForm(): void {
    if (this.inviteDerivedBrand) {
      return;
    }

    const selectedPartnerId = `${this.form.get('brand')?.value || ''}`.trim();
    this.partnerId = selectedPartnerId || undefined;
    this.partner = selectedPartnerId
      ? this.partners.find((partner) => `${partner?.partnerId || ''}`.trim() === selectedPartnerId)
      : undefined;
  }

  public getPartnerLabel(partner: Partner): string {
    return `${partner?.name || partner?.shortName || partner?.partnerId || ''}`.trim();
  }

  private normalizeEmail(email: string): string {
    return `${email || ''}`.trim().toLowerCase();
  }
}
